import { useEffect, useState } from "react";
import { Search, Users, Utensils, Tag, MapPin } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { toast } from "@/lib/toast";
import { AnimatePresence, motion } from "framer-motion";
import CitySelect from "@/components/CitySelect";
import PillSelect from "@/components/PillSelect";
import { useCity } from "@/context/CityContext";
import { CATEGORIES, hasCuisine, hasCapacity, KEYWORD_HINTS } from "@/lib/categories";

const CAT_OPTIONS = CATEGORIES.map((c) => ({ value: c.key, label: c.plural, hint: c.blurb, Icon: c.Icon }));
const VEG_OPTIONS = [{ value: "any", label: "Veg & non-veg", hint: "Show every menu" }, { value: "veg", label: "Pure veg only", hint: "Vegetarian kitchens & menus" }];

function Cell({ icon: I, label, children, className = "", missing = false }) {
  return (
    <div className={`group flex items-center gap-3 px-4 py-3.5 md:py-4 rounded-[20px] transition-colors min-w-0 ${missing ? "bg-rose-50 ring-2 ring-inset ring-rose-300" : "hover:bg-[color:var(--ivory)] focus-within:bg-[color:var(--ivory)]"} ${className}`} data-missing={missing || undefined}>
      <I className={`w-5 h-5 shrink-0 ${missing ? "text-rose-500" : "text-[color:var(--gold)]"}`}/>
      <div className="flex-1 min-w-0">
        <div className={`text-[13px] font-semibold leading-none mb-1.5 ${missing ? "text-rose-700" : "text-[color:var(--maroon)]"}`}>{label}</div>
        {children}
      </div>
    </div>
  );
}

function KeywordInput({ value, onChange }) {
  const [i, setI] = useState(0);
  const [focus, setFocus] = useState(false);
  useEffect(() => { const t = setInterval(() => setI((x) => (x + 1) % KEYWORD_HINTS.length), 2200); return () => clearInterval(t); }, []);
  return (
    <div className="relative">
      <input value={value} onChange={(e) => onChange(e.target.value)} onFocus={() => setFocus(true)} onBlur={() => setFocus(false)}
        aria-label="Keywords" className="w-full bg-transparent outline-none text-[16px] text-[color:var(--ink)] font-semibold py-1" data-testid="search-q"/>
      {!value && (
        <span className="pointer-events-none absolute inset-0 flex items-center overflow-hidden text-[16px] text-neutral-500" aria-hidden>
          <span className="shrink-0">{focus ? "Type, or try" : "Try"}&nbsp;</span>
          <span className="relative h-[1.5em] flex-1 overflow-hidden">
            <AnimatePresence initial={false}>
              <motion.span key={i} initial={{ y: "100%", opacity: 0 }} animate={{ y: "0%", opacity: 1 }} exit={{ y: "-100%", opacity: 0 }} transition={{ duration: 0.35 }}
                className="absolute left-0 top-0 leading-[1.5em] whitespace-nowrap text-[color:var(--maroon)]/80" data-testid="search-q-hint">“{KEYWORD_HINTS[i]}”</motion.span>
            </AnimatePresence>
          </span>
        </span>
      )}
    </div>
  );
}

export default function MasterSearch({ defaultCity, defaultCategory = "", hero = false, onSearch }) {
  const { city: ctxCity, setCity: setCtxCity } = useCity();
  const [city, setCity] = useState(defaultCity ?? ctxCity ?? "");
  const [pax, setPax] = useState("");
  const [veg, setVeg] = useState("any");
  const [cat, setCat] = useState(defaultCategory || "");
  const [q, setQ] = useState("");
  const [missing, setMissing] = useState({});
  const nav = useNavigate();

  useEffect(() => { if (defaultCity !== undefined) setCity(defaultCity); }, [defaultCity]);
  useEffect(() => { setCat(defaultCategory || ""); }, [defaultCategory]);

  const cuisineOk = hasCuisine(cat);
  const capacityOk = hasCapacity(cat);

  // every field that applies to the chosen category must be filled (keywords stay optional:
  // a made-up keyword would hide good results)
  useEffect(() => { setMissing((m) => (Object.keys(m).length ? { city: m.city && !city, cat: m.cat && !cat, pax: m.pax && capacityOk && !pax } : m)); }, [city, cat, pax, capacityOk]);

  const submit = (e) => {
    e?.preventDefault();
    const miss = { city: !city, cat: !cat, pax: capacityOk && !(Number(pax) > 0) };
    if (miss.city || miss.cat || miss.pax) {
      setMissing(miss);
      toast.error("Enter all details to search");
      return;
    }
    setMissing({});
    const params = new URLSearchParams();
    if (city) { params.set("city", city); setCtxCity(city); }
    if (pax && capacityOk) params.set("pax_min", pax);
    if (veg === "veg" && cuisineOk) params.set("veg", "true");
    if (cat) params.set("category", cat);
    if (q.trim()) params.set("q", q.trim());
    onSearch?.();
    nav(`/properties?${params.toString()}`);
  };

  return (
    <form onSubmit={submit} className={`bg-white border border-[color:var(--line)] rounded-[30px] p-2 md:p-2.5 ${hero ? "shadow-[0_30px_80px_rgba(42,15,17,0.35)]" : "shadow-[0_14px_40px_rgba(77,14,18,0.10)]"}`} data-testid="master-search">
      <div className="grid grid-cols-2 xl:grid-cols-[1.1fr_1.2fr_0.85fr_1.3fr_1.45fr_auto] gap-1 items-stretch">
        <Cell icon={MapPin} label="City" className="col-span-2 md:col-span-1" missing={missing.city}>
          <CitySelect value={city} onChange={setCity} variant="bare" placeholder="Choose city" testId="search-city"/>
        </Cell>
        <Cell icon={Tag} label="Category" className="col-span-2 md:col-span-1" missing={missing.cat}>
          <PillSelect value={cat} onChange={setCat} options={CAT_OPTIONS} variant="bare" placeholder="Choose category" testId="search-category" minWidth={300}/>
        </Cell>
        <Cell icon={Users} label="Guests" className={`col-span-2 sm:col-span-1 ${capacityOk ? "" : "opacity-50"}`} missing={missing.pax}>
          <input type="number" inputMode="numeric" min="1" value={capacityOk ? pax : ""} disabled={!capacityOk} onChange={(e) => setPax(e.target.value)}
            placeholder={capacityOk ? "e.g. 300" : "Not needed"} className="w-full bg-transparent outline-none text-[16px] text-[color:var(--ink)] font-semibold placeholder:text-neutral-500 placeholder:font-normal py-1 disabled:cursor-not-allowed" data-testid="search-pax"/>
        </Cell>
        <Cell icon={Utensils} label="Cuisine" className={`col-span-2 sm:col-span-1 ${cuisineOk ? "" : "opacity-50"}`}>
          <PillSelect value={cuisineOk ? veg : ""} onChange={setVeg} options={VEG_OPTIONS} variant="bare" disabled={!cuisineOk} placeholder="Not needed" testId="search-veg"/>
        </Cell>
        <Cell icon={Search} label="Keywords" className="col-span-2 xl:col-span-1">
          <KeywordInput value={q} onChange={setQ}/>
        </Cell>
        <button type="submit" className={`btn-wine col-span-2 xl:col-span-1 !rounded-[22px] ${hero ? "!px-9 !py-5 xl:!py-0 text-[17px]" : "!px-8 !py-4 xl:!py-0 text-base"}`} data-testid="search-submit">
          <Search className="w-5 h-5"/> Search
        </button>
      </div>
    </form>
  );
}
